import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useBrand } from '../context/BrandContext';
import { useLanguage } from '../context/LanguageContext';

type LegalKind = 'impressum' | 'datenschutz' | 'agb' | 'cookies';

const pathToKind = (pathname: string): LegalKind | undefined => {
  const p = pathname.toLowerCase();
  if (p.includes('impressum')) return 'impressum';
  if (p.includes('datenschutz') || p.includes('privacy')) return 'datenschutz';
  if (p.includes('agb') || p.includes('terms')) return 'agb';
  if (p.includes('cookies')) return 'cookies';
  return undefined;
};

const COMPANY = {
  legalName: 'Sushiki UG (haftungsbeschränkt)',
  // Registered office / Sitz der Gesellschaft (confirmed by Sadik 29.07.2026)
  street: 'Moosweg 3',
  zip: '51373',
  city: 'Leverkusen',
  country: { de: 'Deutschland', en: 'Germany' },
  phone: '+49 2233 7063324',
  email: 'info@sushiki-huerth.de',
  managingDirector: 'Sadik Demir',
  registryCourt: { de: 'Amtsgericht Köln', en: 'District Court of Cologne' },
  registryNumber: 'HRB — (wird ergänzt)',
  vatId: 'DE356916687',
  responsible: 'Sadik Demir',
};

const LegalPage: React.FC = () => {
  const params = useParams<{ kind?: LegalKind }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { lang } = useLanguage();
  const langKey = lang === 'de' ? 'de' : 'en';

  const kind: LegalKind | undefined = params.kind || pathToKind(location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [kind]);

  const titles: Record<LegalKind, { de: string; en: string }> = {
    impressum: { de: 'Impressum', en: 'Legal Notice' },
    datenschutz: { de: 'Datenschutzerklärung', en: 'Privacy Policy' },
    agb: { de: 'Allgemeine Geschäftsbedingungen', en: 'Terms & Conditions' },
    cookies: { de: 'Cookie-Richtlinie', en: 'Cookie Policy' },
  };

  const title = kind && titles[kind] ? titles[kind][langKey] : 'Legal';

  const h2: React.CSSProperties = {
    fontFamily: brand.fontDisplay,
    fontSize: '1.4rem',
    fontWeight: 700,
    color: brand.text,
    margin: '36px 0 14px',
    letterSpacing: '-0.01em',
  };
  const h3: React.CSSProperties = {
    fontFamily: brand.fontDisplay,
    fontSize: '1.08rem',
    fontWeight: 700,
    color: brand.text,
    margin: '22px 0 8px',
  };
  const p: React.CSSProperties = {
    color: brand.textMuted,
    fontSize: '0.95rem',
    lineHeight: 1.7,
    margin: '0 0 12px',
  };
  const ul: React.CSSProperties = {
    color: brand.textMuted,
    fontSize: '0.95rem',
    lineHeight: 1.75,
    paddingLeft: 22,
    margin: '0 0 12px',
  };
  const strong: React.CSSProperties = { color: brand.text, fontWeight: 600 };
  const link: React.CSSProperties = { color: brand.primary, textDecoration: 'none' };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: brand.bg,
        color: brand.text,
        fontFamily: brand.fontBody,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: `${brand.bg}f2`,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${brand.text}12`,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          style={{
            background: 'transparent',
            border: `1px solid ${brand.text}22`,
            color: brand.text,
            borderRadius: 999,
            width: 38,
            height: 38,
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            fontSize: '1.1rem',
          }}
        >
          ←
        </button>
        <div
          style={{
            fontFamily: brand.fontDisplay,
            fontSize: '1.05rem',
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          {title}
        </div>
      </div>

      <div
        style={{
          maxWidth: 820,
          margin: '0 auto',
          padding: '36px 22px 80px',
        }}
      >
        <p style={{ ...p, fontSize: '0.78rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: brand.primary, margin: '0 0 6px' }}>
          {brand.name} · {COMPANY.city}
        </p>
        <h1
          style={{
            fontFamily: brand.fontDisplay,
            fontSize: 'clamp(1.8rem, 5vw, 2.4rem)',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            margin: '0 0 8px',
          }}
        >
          {title}
        </h1>
        <p style={{ ...p, margin: '0 0 24px' }}>
          {langKey === 'de'
            ? `Stand: ${new Date().toLocaleDateString('de-DE', { year: 'numeric', month: 'long' })}`
            : `Last updated: ${new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long' })}`}
        </p>

        {kind === 'impressum' && <Impressum langKey={langKey} styles={{ h2, h3, p, ul, strong, link }} />}
        {kind === 'datenschutz' && <Datenschutz langKey={langKey} styles={{ h2, h3, p, ul, strong, link }} />}
        {kind === 'agb' && <AGB langKey={langKey} styles={{ h2, h3, p, ul, strong, link }} />}
        {kind === 'cookies' && <Cookies langKey={langKey} styles={{ h2, h3, p, ul, strong, link }} />}
      </div>
    </div>
  );
};

type S = {
  h2: React.CSSProperties;
  h3: React.CSSProperties;
  p: React.CSSProperties;
  ul: React.CSSProperties;
  strong: React.CSSProperties;
  link: React.CSSProperties;
};

// ─────────────────────────────────────────────
// IMPRESSUM — per § 5 TMG / § 18 MStV
// ─────────────────────────────────────────────
const Impressum: React.FC<{ langKey: 'de' | 'en'; styles: S }> = ({ langKey, styles: s }) => {
  if (langKey === 'de') {
    return (
      <>
        <h2 style={s.h2}>Angaben gemäß § 5 TMG</h2>
        <p style={s.p}>
          <span style={s.strong}>{COMPANY.legalName}</span><br />
          {COMPANY.street}<br />
          {COMPANY.zip} {COMPANY.city}<br />
          {COMPANY.country.de}
        </p>

        <h3 style={s.h3}>Vertreten durch</h3>
        <p style={s.p}>Geschäftsführer: {COMPANY.managingDirector}</p>

        <h3 style={s.h3}>Kontakt</h3>
        <p style={s.p}>
          Telefon: <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} style={s.link}>{COMPANY.phone}</a><br />
          E-Mail: <a href={`mailto:${COMPANY.email}`} style={s.link}>{COMPANY.email}</a>
        </p>

        <h3 style={s.h3}>Registereintrag</h3>
        <p style={s.p}>
          Eintragung im Handelsregister<br />
          Registergericht: {COMPANY.registryCourt.de}<br />
          Registernummer: {COMPANY.registryNumber}
        </p>

        <h3 style={s.h3}>Umsatzsteuer-ID</h3>
        <p style={s.p}>
          Umsatzsteuer-Identifikationsnummer gemäß § 27 a Umsatzsteuergesetz:<br />
          {COMPANY.vatId}
        </p>

        <h3 style={s.h3}>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h3>
        <p style={s.p}>
          {COMPANY.responsible}<br />
          {COMPANY.street}, {COMPANY.zip} {COMPANY.city}
        </p>

        <h2 style={s.h2}>EU-Streitschlichtung</h2>
        <p style={s.p}>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
          <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" style={s.link}>
            https://ec.europa.eu/consumers/odr/
          </a>
          <br />
          Unsere E-Mail-Adresse finden Sie oben im Impressum.
        </p>

        <h2 style={s.h2}>Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
        <p style={s.p}>
          Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
          teilzunehmen.
        </p>

        <h2 style={s.h2}>Haftung für Inhalte</h2>
        <p style={s.p}>
          Als Diensteanbieter sind wir gemäß § 7 Abs.1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen
          Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte
          oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige
          Tätigkeit hinweisen.
        </p>

        <h2 style={s.h2}>Haftung für Links</h2>
        <p style={s.p}>
          Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb
          können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist
          stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.
        </p>

        <h2 style={s.h2}>Urheberrecht</h2>
        <p style={s.p}>
          Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen
          Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der
          Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
        </p>
      </>
    );
  }
  return (
    <>
      <h2 style={s.h2}>Information pursuant to § 5 TMG</h2>
      <p style={s.p}>
        <span style={s.strong}>{COMPANY.legalName}</span><br />
        {COMPANY.street}<br />
        {COMPANY.zip} {COMPANY.city}<br />
        {COMPANY.country.en}
      </p>

      <h3 style={s.h3}>Represented by</h3>
      <p style={s.p}>Managing Director: {COMPANY.managingDirector}</p>

      <h3 style={s.h3}>Contact</h3>
      <p style={s.p}>
        Phone: <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} style={s.link}>{COMPANY.phone}</a><br />
        Email: <a href={`mailto:${COMPANY.email}`} style={s.link}>{COMPANY.email}</a>
      </p>

      <h3 style={s.h3}>Commercial Register</h3>
      <p style={s.p}>
        Entry in the commercial register<br />
        Registry court: {COMPANY.registryCourt.en}<br />
        Registry number: {COMPANY.registryNumber}
      </p>

      <h3 style={s.h3}>VAT Identification</h3>
      <p style={s.p}>
        VAT ID pursuant to § 27 a of the German VAT Act:<br />
        {COMPANY.vatId}
      </p>

      <h3 style={s.h3}>Responsible for content under § 18 (2) MStV</h3>
      <p style={s.p}>
        {COMPANY.responsible}<br />
        {COMPANY.street}, {COMPANY.zip} {COMPANY.city}
      </p>

      <h2 style={s.h2}>EU Online Dispute Resolution</h2>
      <p style={s.p}>
        The European Commission provides a platform for online dispute resolution (ODR):{' '}
        <a href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer" style={s.link}>
          https://ec.europa.eu/consumers/odr/
        </a>
        <br />
        You can find our email address above.
      </p>

      <h2 style={s.h2}>Consumer Dispute Resolution</h2>
      <p style={s.p}>
        We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board.
      </p>

      <h2 style={s.h2}>Liability for Content</h2>
      <p style={s.p}>
        As a service provider, we are responsible for our own content on these pages in accordance with general laws
        pursuant to § 7 (1) TMG. However, under §§ 8 to 10 TMG, we are not obliged to monitor transmitted or stored
        third-party information or to investigate circumstances indicating illegal activity.
      </p>

      <h2 style={s.h2}>Liability for Links</h2>
      <p style={s.p}>
        Our offer contains links to external third-party websites, the contents of which we have no influence over.
        Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the
        pages is always responsible for the content of the linked pages.
      </p>

      <h2 style={s.h2}>Copyright</h2>
      <p style={s.p}>
        The content and works on these pages created by the site operators are subject to German copyright law. Duplication,
        processing, distribution and any kind of exploitation outside the limits of copyright require the written consent
        of the respective author or creator.
      </p>
    </>
  );
};

// ─────────────────────────────────────────────
// DATENSCHUTZ — DSGVO / GDPR
// ─────────────────────────────────────────────
const Datenschutz: React.FC<{ langKey: 'de' | 'en'; styles: S }> = ({ langKey, styles: s }) => {
  if (langKey === 'de') {
    return (
      <>
        <h2 style={s.h2}>1. Verantwortlicher</h2>
        <p style={s.p}>
          Verantwortlicher für die Datenverarbeitung auf dieser Website im Sinne der Datenschutz-Grundverordnung (DSGVO):
        </p>
        <p style={s.p}>
          <span style={s.strong}>{COMPANY.legalName}</span><br />
          {COMPANY.street}, {COMPANY.zip} {COMPANY.city}<br />
          E-Mail: <a href={`mailto:${COMPANY.email}`} style={s.link}>{COMPANY.email}</a><br />
          Telefon: <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} style={s.link}>{COMPANY.phone}</a>
        </p>

        <h2 style={s.h2}>2. Erhebung und Speicherung personenbezogener Daten</h2>
        <h3 style={s.h3}>a) Beim Besuch der Website</h3>
        <p style={s.p}>
          Beim Aufrufen unserer Website werden durch den auf Ihrem Endgerät zum Einsatz kommenden Browser automatisch
          Informationen an den Server unserer Website gesendet (Server-Logfiles): IP-Adresse, Datum und Uhrzeit der
          Anfrage, Zeitzonendifferenz zur GMT, Inhalt der Anforderung, Zugriffsstatus/HTTP-Statuscode, übertragene
          Datenmenge, Website (Referrer), Browser, Betriebssystem, Sprache und Version der Browsersoftware.
        </p>
        <p style={s.p}>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer stabilen und sicheren Website).
        </p>

        <h3 style={s.h3}>b) Bei Nutzung unseres Bestellsystems</h3>
        <p style={s.p}>
          Bei einer Bestellung erheben wir: Name, Lieferadresse, Telefonnummer, E-Mail-Adresse, Bestelldetails,
          Zahlungsinformationen. Diese Daten werden zur Abwicklung Ihrer Bestellung verarbeitet.
        </p>
        <p style={s.p}>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).
        </p>

        <h3 style={s.h3}>c) Bei Nutzung unserer mobilen Apps (SushiKi App und SushiKi Team App)</h3>
        <p style={s.p}>
          Diese Datenschutzerklärung gilt auch für unsere mobilen Apps.
        </p>
        <ul style={s.ul}>
          <li>
            <span style={s.strong}>SushiKi App (Bestell-App für Kundinnen und Kunden):</span> Bei Registrierung und
            Bestellung verarbeiten wir Name, E-Mail-Adresse, Telefonnummer, Lieferadresse und Bestelldaten. Bei
            Lieferbestellungen können Sie Ihren Lieferort auf einer Karte auswählen; die gewählten Standortkoordinaten
            werden ausschließlich zur Zustellung Ihrer Bestellung verarbeitet. Die Kartendarstellung erfolgt über
            OpenStreetMap; beim Laden der Karte wird Ihre IP-Adresse an die OpenStreetMap Foundation übertragen.
          </li>
          <li>
            <span style={s.strong}>SushiKi Team App (App für Beschäftigte):</span> Für Beschäftigte verarbeiten wir
            Name, E-Mail-Adresse, Arbeitszeiten (Ein- und Ausstempeln), Schicht- und Aufgabendaten einschließlich
            hochgeladener Foto-Nachweise sowie bei Lieferfahrten Standortdaten während der Auslieferung
            (§ 26 BDSG in Verbindung mit Art. 6 Abs. 1 lit. b DSGVO).
          </li>
          <li>
            <span style={s.strong}>Push-Benachrichtigungen:</span> Mit Ihrer Einwilligung senden wir
            Push-Benachrichtigungen (z. B. Bestellstatus, Aufgaben). Sie können die Einwilligung jederzeit in den
            Einstellungen Ihres Geräts widerrufen.
          </li>
        </ul>
        <p style={s.p}>
          Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung); für Beschäftigtendaten § 26 BDSG. Eine
          Verwendung dieser Daten zu Werbe- oder Tracking-Zwecken findet nicht statt.
        </p>

        <h3 style={s.h3}>d) Konto und Daten löschen (SushiKi App)</h3>
        <p style={s.p}>
          Sie können Ihr Kundenkonto in der SushiKi App jederzeit selbst löschen: Öffnen Sie in der App
          <span style={s.strong}> Profil → Konto löschen</span> und bestätigen Sie die Löschung mit Ihrem Passwort.
          Dabei werden Ihr Konto, Ihre Profildaten (Name, E-Mail-Adresse, Telefonnummer, Adressen) und Ihre
          Anmeldedaten dauerhaft gelöscht. Alternativ können Sie die Löschung per E-Mail an
          <a href="mailto:info@sushiki.de" style={s.link}> info@sushiki.de</a> beantragen.
        </p>
        <p style={s.p}>
          Abgeschlossene Bestell- und Rechnungsdaten bewahren wir auch nach der Kontolöschung auf, soweit
          handels- und steuerrechtliche Aufbewahrungspflichten bestehen (§ 147 AO, § 257 HGB: bis zu 10 Jahre);
          diese Daten werden nach Ablauf der Fristen gelöscht.
        </p>

        <h2 style={s.h2}>3. Weitergabe von Daten</h2>
        <p style={s.p}>
          Ihre Daten werden an folgende Empfänger weitergegeben, soweit dies für die Leistungserbringung erforderlich ist:
        </p>
        <ul style={s.ul}>
          <li>Lieferfahrer (Name, Adresse, Telefonnummer zur Zustellung)</li>
          <li>Zahlungsdienstleister (z. B. Stripe, PayPal) für die Zahlungsabwicklung</li>
          <li>Google Firebase (Authentifizierung, Datenbank, Hosting) — Server in der EU</li>
          <li>Steuerberater und Finanzamt, soweit gesetzlich vorgeschrieben</li>
        </ul>

        <h2 style={s.h2}>4. Cookies</h2>
        <p style={s.p}>
          Wir verwenden auf unserer Website technisch notwendige Cookies (Warenkorb, Session, Spracheinstellung) sowie
          bei entsprechender Einwilligung Analyse-Cookies. Details finden Sie in unserer{' '}
          <a href="/cookies" style={s.link}>Cookie-Richtlinie</a>.
        </p>

        <h2 style={s.h2}>5. Speicherdauer</h2>
        <p style={s.p}>
          Bestelldaten werden zur Erfüllung steuer- und handelsrechtlicher Aufbewahrungspflichten gemäß § 147 AO bzw.
          § 257 HGB für 10 Jahre gespeichert. Server-Logfiles werden nach 14 Tagen gelöscht.
        </p>

        <h2 style={s.h2}>6. Ihre Rechte</h2>
        <p style={s.p}>Sie haben gemäß DSGVO folgende Rechte:</p>
        <ul style={s.ul}>
          <li>Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)</li>
          <li>Berichtigung unrichtiger Daten (Art. 16 DSGVO)</li>
          <li>Löschung (Art. 17 DSGVO)</li>
          <li>Einschränkung der Verarbeitung (Art. 18 DSGVO)</li>
          <li>Datenübertragbarkeit (Art. 20 DSGVO)</li>
          <li>Widerspruch gegen die Verarbeitung (Art. 21 DSGVO)</li>
          <li>Beschwerde bei einer Aufsichtsbehörde (Art. 77 DSGVO)</li>
        </ul>
        <p style={s.p}>
          Zur Ausübung Ihrer Rechte wenden Sie sich bitte an:{' '}
          <a href={`mailto:${COMPANY.email}`} style={s.link}>{COMPANY.email}</a>
        </p>

        <h2 style={s.h2}>7. Zuständige Aufsichtsbehörde</h2>
        <p style={s.p}>
          Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen<br />
          Kavalleriestraße 2-4, 40213 Düsseldorf<br />
          <a href="https://www.ldi.nrw.de" target="_blank" rel="noopener noreferrer" style={s.link}>www.ldi.nrw.de</a>
        </p>

        <h2 style={s.h2}>8. SSL-Verschlüsselung</h2>
        <p style={s.p}>
          Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung vertraulicher Inhalte eine
          SSL-/TLS-Verschlüsselung. Sie erkennen eine verschlüsselte Verbindung an der Adresszeile „https://“.
        </p>
      </>
    );
  }
  return (
    <>
      <h2 style={s.h2}>1. Controller</h2>
      <p style={s.p}>
        The controller responsible for data processing on this website within the meaning of the General Data Protection
        Regulation (GDPR) is:
      </p>
      <p style={s.p}>
        <span style={s.strong}>{COMPANY.legalName}</span><br />
        {COMPANY.street}, {COMPANY.zip} {COMPANY.city}<br />
        Email: <a href={`mailto:${COMPANY.email}`} style={s.link}>{COMPANY.email}</a><br />
        Phone: <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} style={s.link}>{COMPANY.phone}</a>
      </p>

      <h2 style={s.h2}>2. Collection and Storage of Personal Data</h2>
      <h3 style={s.h3}>a) When visiting the website</h3>
      <p style={s.p}>
        When you access our website, your browser automatically sends information to our server (server log files):
        IP address, date and time of the request, time zone difference to GMT, content of the request, HTTP status code,
        amount of data transferred, referrer, browser, operating system, language and version of the browser software.
      </p>
      <p style={s.p}>Legal basis: Art. 6 (1)(f) GDPR (legitimate interest in a stable and secure website).</p>

      <h3 style={s.h3}>b) When using our ordering system</h3>
      <p style={s.p}>
        When you place an order, we collect: name, delivery address, phone number, email address, order details, payment
        information. These data are processed for the purpose of fulfilling your order.
      </p>
      <p style={s.p}>Legal basis: Art. 6 (1)(b) GDPR (performance of a contract).</p>

      <h3 style={s.h3}>c) When using our mobile apps (SushiKi app and SushiKi Team app)</h3>
      <p style={s.p}>This privacy policy also applies to our mobile apps.</p>
      <ul style={s.ul}>
        <li>
          <span style={s.strong}>SushiKi app (ordering app for customers):</span> When you register and place orders we
          process your name, email address, phone number, delivery address and order data. For delivery orders you can
          select your delivery location on a map; the selected coordinates are processed solely to deliver your order.
          The map is provided by OpenStreetMap; when the map loads, your IP address is transmitted to the OpenStreetMap
          Foundation.
        </li>
        <li>
          <span style={s.strong}>SushiKi Team app (app for employees):</span> For employees we process name, email
          address, working hours (clock-in and clock-out), shift and task data including uploaded photo proof, and, for
          delivery runs, location data during the delivery (Section 26 German Federal Data Protection Act (BDSG) in
          conjunction with Art. 6 (1)(b) GDPR).
        </li>
        <li>
          <span style={s.strong}>Push notifications:</span> With your consent we send push notifications (e.g. order
          status, tasks). You can revoke this consent at any time in your device settings.
        </li>
      </ul>
      <p style={s.p}>
        Legal basis: Art. 6 (1)(b) GDPR (performance of a contract); for employee data Section 26 BDSG. This data is not
        used for advertising or tracking purposes.
      </p>

      <h3 style={s.h3}>d) Deleting your account and data (SushiKi app)</h3>
      <p style={s.p}>
        You can delete your customer account in the SushiKi app yourself at any time: open
        <span style={s.strong}> Profile → Delete Account</span> in the app and confirm with your password. This
        permanently deletes your account, your profile data (name, email address, phone number, addresses) and your
        login credentials. Alternatively, you can request deletion by email to
        <a href="mailto:info@sushiki.de" style={s.link}> info@sushiki.de</a>.
      </p>
      <p style={s.p}>
        Completed order and invoice data is retained after account deletion only as far as commercial and tax law
        requires (Section 147 AO, Section 257 HGB: up to 10 years) and is deleted once these periods expire.
      </p>

      <h2 style={s.h2}>3. Disclosure of Data</h2>
      <p style={s.p}>Your data is shared with the following recipients as necessary for service delivery:</p>
      <ul style={s.ul}>
        <li>Delivery drivers (name, address, phone number for delivery)</li>
        <li>Payment service providers (e.g. Stripe, PayPal) for payment processing</li>
        <li>Google Firebase (authentication, database, hosting) — servers located in the EU</li>
        <li>Tax advisors and tax authorities, where legally required</li>
      </ul>

      <h2 style={s.h2}>4. Cookies</h2>
      <p style={s.p}>
        We use strictly necessary cookies (cart, session, language preference) and, with your consent, analytics cookies.
        See our <a href="/cookies" style={s.link}>Cookie Policy</a> for details.
      </p>

      <h2 style={s.h2}>5. Retention Period</h2>
      <p style={s.p}>
        Order data is retained for 10 years to comply with tax and commercial law (§ 147 AO, § 257 HGB). Server log files
        are deleted after 14 days.
      </p>

      <h2 style={s.h2}>6. Your Rights</h2>
      <p style={s.p}>Under the GDPR, you have the following rights:</p>
      <ul style={s.ul}>
        <li>Access to your stored data (Art. 15 GDPR)</li>
        <li>Rectification of inaccurate data (Art. 16 GDPR)</li>
        <li>Erasure (Art. 17 GDPR)</li>
        <li>Restriction of processing (Art. 18 GDPR)</li>
        <li>Data portability (Art. 20 GDPR)</li>
        <li>Objection to processing (Art. 21 GDPR)</li>
        <li>Complaint to a supervisory authority (Art. 77 GDPR)</li>
      </ul>
      <p style={s.p}>
        To exercise your rights, please contact:{' '}
        <a href={`mailto:${COMPANY.email}`} style={s.link}>{COMPANY.email}</a>
      </p>

      <h2 style={s.h2}>7. Competent Supervisory Authority</h2>
      <p style={s.p}>
        Landesbeauftragte für Datenschutz und Informationsfreiheit Nordrhein-Westfalen<br />
        Kavalleriestraße 2-4, 40213 Düsseldorf, Germany<br />
        <a href="https://www.ldi.nrw.de" target="_blank" rel="noopener noreferrer" style={s.link}>www.ldi.nrw.de</a>
      </p>

      <h2 style={s.h2}>8. SSL Encryption</h2>
      <p style={s.p}>
        This site uses SSL/TLS encryption for security and to protect the transmission of confidential content. You can
        recognize an encrypted connection by the “https://” in the address bar.
      </p>
    </>
  );
};

// ─────────────────────────────────────────────
// AGB — General Terms & Conditions
// ─────────────────────────────────────────────
const AGB: React.FC<{ langKey: 'de' | 'en'; styles: S }> = ({ langKey, styles: s }) => {
  if (langKey === 'de') {
    return (
      <>
        <h2 style={s.h2}>§ 1 Geltungsbereich</h2>
        <p style={s.p}>
          Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Bestellungen, die über unsere Website, unsere
          mobile App oder telefonisch bei {COMPANY.legalName} („wir“) aufgegeben werden. Abweichende Bedingungen des
          Kunden werden nicht anerkannt, es sei denn, wir stimmen ihrer Geltung ausdrücklich schriftlich zu.
        </p>

        <h2 style={s.h2}>§ 2 Vertragspartner, Vertragsschluss</h2>
        <p style={s.p}>
          Der Kaufvertrag kommt zustande mit {COMPANY.legalName}. Die Darstellung der Produkte auf unserer Website
          stellt kein rechtlich bindendes Angebot, sondern eine Aufforderung zur Bestellung dar. Mit Absenden Ihrer
          Bestellung geben Sie ein verbindliches Angebot ab. Der Vertrag kommt durch unsere Auftragsbestätigung
          (per E-Mail oder Anzeige in der App) zustande.
        </p>

        <h2 style={s.h2}>§ 3 Preise und Versandkosten</h2>
        <p style={s.p}>
          Alle Preise sind Endpreise inklusive der gesetzlichen Umsatzsteuer (7 % bei Speisen zum Mitnehmen/Lieferung,
          19 % bei Getränken und Verzehr im Restaurant). Liefergebühren werden vor Bestellabschluss transparent
          angezeigt. Ab einem Bestellwert von 25 € liefern wir innerhalb von Hürth versandkostenfrei.
        </p>

        <h2 style={s.h2}>§ 4 Lieferung</h2>
        <p style={s.p}>
          Die Lieferung erfolgt an die vom Kunden angegebene Adresse innerhalb unseres Liefergebietes (Hürth und
          Umgebung). Die voraussichtliche Lieferzeit beträgt 30–45 Minuten, kann aber je nach Bestellaufkommen und
          Wetter variieren. Der Kunde ist dafür verantwortlich, dass eine Zustellung möglich ist; entstehen durch
          einen erfolglosen Zustellversuch Kosten, können diese dem Kunden in Rechnung gestellt werden.
        </p>

        <h2 style={s.h2}>§ 5 Zahlung</h2>
        <p style={s.p}>
          Die Zahlung erfolgt wahlweise per Barzahlung bei Lieferung, EC-/Kreditkarte bei Lieferung, oder online über
          die angebotenen Zahlungsdienstleister. Bei Online-Zahlung ist der Betrag mit Vertragsschluss fällig.
        </p>

        <h2 style={s.h2}>§ 6 Widerrufsrecht</h2>
        <p style={s.p}>
          <span style={s.strong}>Hinweis:</span> Das Widerrufsrecht besteht gemäß § 312g Abs. 2 Nr. 2 BGB{' '}
          <span style={s.strong}>nicht</span> bei Verträgen zur Lieferung von Waren, die schnell verderben können oder
          deren Verfallsdatum schnell überschritten würde. Dies gilt für unsere Speisen.
        </p>
        <p style={s.p}>
          Sie können eine aufgegebene Bestellung dennoch bis zur Annahme durch die Küche (ca. 5 Minuten nach Eingang)
          kostenfrei stornieren. Danach ist eine Stornierung nur nach Rücksprache möglich.
        </p>

        <h2 style={s.h2}>§ 7 Altersbeschränkung bei Alkohol</h2>
        <p style={s.p}>
          Die Lieferung alkoholischer Getränke erfolgt nur an Personen, die das gesetzliche Mindestalter erreicht
          haben (Bier/Wein ab 16 Jahren, Spirituosen ab 18 Jahren). Der Lieferfahrer ist berechtigt, einen
          Altersnachweis zu verlangen und die Herausgabe bei Zweifeln zu verweigern.
        </p>

        <h2 style={s.h2}>§ 8 Allergene & Lebensmittelsicherheit</h2>
        <p style={s.p}>
          Informationen zu Allergenen (Lebensmittelinformationsverordnung – LMIV) werden auf Anfrage und in unserer
          Speisekarte bereitgestellt. Bei Unverträglichkeiten oder Allergien kontaktieren Sie uns bitte vor der
          Bestellung. Eine vollständige Vermeidung von Kreuzkontaminationen in unserer Küche kann nicht garantiert
          werden.
        </p>

        <h2 style={s.h2}>§ 9 Gewährleistung</h2>
        <p style={s.p}>
          Es gelten die gesetzlichen Gewährleistungsrechte. Reklamationen sind unverzüglich nach Erhalt der Lieferung
          per Telefon an {COMPANY.phone} zu melden.
        </p>

        <h2 style={s.h2}>§ 10 Haftung</h2>
        <p style={s.p}>
          Wir haften unbeschränkt für Vorsatz und grobe Fahrlässigkeit sowie für Schäden aus der Verletzung des Lebens,
          des Körpers oder der Gesundheit. Bei leichter Fahrlässigkeit haften wir nur bei Verletzung einer wesentlichen
          Vertragspflicht, beschränkt auf den vertragstypischen, vorhersehbaren Schaden.
        </p>

        <h2 style={s.h2}>§ 11 Schlussbestimmungen</h2>
        <p style={s.p}>
          Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts. Sollten einzelne
          Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.
        </p>
      </>
    );
  }
  return (
    <>
      <h2 style={s.h2}>§ 1 Scope</h2>
      <p style={s.p}>
        These General Terms and Conditions (“Terms”) apply to all orders placed through our website, mobile app, or by
        telephone with {COMPANY.legalName} (“we”, “us”). Any deviating terms of the customer shall not apply unless we
        expressly agree to them in writing.
      </p>

      <h2 style={s.h2}>§ 2 Contract Partner, Conclusion of Contract</h2>
      <p style={s.p}>
        The sales contract is concluded with {COMPANY.legalName}. The presentation of products on our website does not
        constitute a legally binding offer, but an invitation to place an order. By submitting your order, you make a
        binding offer. The contract is concluded by our order confirmation (by email or displayed in the app).
      </p>

      <h2 style={s.h2}>§ 3 Prices and Delivery Fees</h2>
      <p style={s.p}>
        All prices are final prices including statutory VAT (7% for takeaway/delivered food, 19% for beverages and
        dine-in). Delivery fees are displayed transparently before checkout. Orders over €25 are delivered free of
        charge within Hürth.
      </p>

      <h2 style={s.h2}>§ 4 Delivery</h2>
      <p style={s.p}>
        Delivery is made to the address provided by the customer within our delivery area (Hürth and surroundings).
        The estimated delivery time is 30–45 minutes but may vary depending on order volume and weather. The customer
        is responsible for ensuring that delivery is possible; costs arising from an unsuccessful delivery attempt may
        be charged to the customer.
      </p>

      <h2 style={s.h2}>§ 5 Payment</h2>
      <p style={s.p}>
        Payment can be made in cash on delivery, by debit/credit card on delivery, or online via the offered payment
        service providers. For online payments, the amount is due upon conclusion of the contract.
      </p>

      <h2 style={s.h2}>§ 6 Right of Withdrawal</h2>
      <p style={s.p}>
        <span style={s.strong}>Note:</span> Pursuant to § 312g (2) No. 2 of the German Civil Code (BGB), the right of
        withdrawal <span style={s.strong}>does not apply</span> to contracts for the delivery of goods that are liable
        to deteriorate rapidly or have a short use-by date. This applies to our food.
      </p>
      <p style={s.p}>
        You may, however, cancel a placed order free of charge until it is accepted by the kitchen (approximately 5
        minutes after receipt). After that, cancellation is only possible after consultation.
      </p>

      <h2 style={s.h2}>§ 7 Alcohol Age Restriction</h2>
      <p style={s.p}>
        Alcoholic beverages are only delivered to persons who have reached the legal minimum age (beer/wine from 16,
        spirits from 18). The delivery driver is entitled to request proof of age and to refuse delivery in case of
        doubt.
      </p>

      <h2 style={s.h2}>§ 8 Allergens & Food Safety</h2>
      <p style={s.p}>
        Information on allergens (pursuant to the Food Information Regulation — LMIV) is provided on request and in
        our menu. For intolerances or allergies, please contact us before placing your order. Complete avoidance of
        cross-contamination in our kitchen cannot be guaranteed.
      </p>

      <h2 style={s.h2}>§ 9 Warranty</h2>
      <p style={s.p}>
        Statutory warranty rights apply. Complaints must be reported immediately upon receipt of the delivery by phone
        at {COMPANY.phone}.
      </p>

      <h2 style={s.h2}>§ 10 Liability</h2>
      <p style={s.p}>
        We are liable without limitation for intent and gross negligence as well as for damages resulting from injury
        to life, body or health. In case of slight negligence, we are only liable for breach of a material contractual
        obligation, limited to damages typical and foreseeable for the contract.
      </p>

      <h2 style={s.h2}>§ 11 Final Provisions</h2>
      <p style={s.p}>
        The law of the Federal Republic of Germany applies to the exclusion of the UN Convention on Contracts for the
        International Sale of Goods. Should individual provisions of these Terms be invalid, the validity of the
        remaining provisions shall remain unaffected.
      </p>
    </>
  );
};

// ─────────────────────────────────────────────
// COOKIES
// ─────────────────────────────────────────────
const Cookies: React.FC<{ langKey: 'de' | 'en'; styles: S }> = ({ langKey, styles: s }) => {
  if (langKey === 'de') {
    return (
      <>
        <h2 style={s.h2}>Was sind Cookies?</h2>
        <p style={s.p}>
          Cookies sind kleine Textdateien, die beim Besuch unserer Website auf Ihrem Endgerät gespeichert werden. Sie
          helfen uns, die Website funktionsfähig zu machen, sie zu verbessern und Ihnen ein besseres Nutzererlebnis zu
          bieten.
        </p>

        <h2 style={s.h2}>Welche Cookies setzen wir ein?</h2>

        <h3 style={s.h3}>1. Technisch notwendige Cookies (immer aktiv)</h3>
        <p style={s.p}>
          Diese Cookies sind für den Betrieb der Website zwingend erforderlich. Sie können nicht abgewählt werden.
        </p>
        <ul style={s.ul}>
          <li><span style={s.strong}>Warenkorb:</span> Speicherung Ihrer ausgewählten Artikel (Dauer: Session)</li>
          <li><span style={s.strong}>Sprache:</span> Ihre gewählte Sprache (Dauer: 12 Monate)</li>
          <li><span style={s.strong}>Authentifizierung (Firebase Auth):</span> Anmeldestatus (Dauer: bis zum Logout)</li>
          <li><span style={s.strong}>Sicherheits-Token (CSRF):</span> Schutz vor Angriffen (Dauer: Session)</li>
        </ul>
        <p style={s.p}>Rechtsgrundlage: Art. 6 Abs. 1 lit. b und f DSGVO, § 25 Abs. 2 TTDSG.</p>

        <h3 style={s.h3}>2. Funktionale Cookies</h3>
        <ul style={s.ul}>
          <li><span style={s.strong}>Standort-Einstellung:</span> Lieferadresse für schnellere Bestellung (Dauer: 30 Tage)</li>
          <li><span style={s.strong}>Letzte Bestellung:</span> Anzeige Ihrer Bestellhistorie (Dauer: 30 Tage)</li>
        </ul>

        <h3 style={s.h3}>3. Analyse-Cookies (nur mit Einwilligung)</h3>
        <p style={s.p}>
          Wir setzen derzeit keine Tracking- oder Marketing-Cookies von Drittanbietern ein. Sollten wir dies in Zukunft
          ändern, werden wir Ihre ausdrückliche Einwilligung einholen (Art. 6 Abs. 1 lit. a DSGVO, § 25 Abs. 1 TTDSG).
        </p>

        <h2 style={s.h2}>Wie kann ich Cookies verwalten?</h2>
        <p style={s.p}>
          Sie können Cookies jederzeit in Ihren Browser-Einstellungen löschen oder deren Speicherung blockieren. Beachten
          Sie, dass unsere Website ohne technisch notwendige Cookies nicht vollständig funktioniert.
        </p>

        <h2 style={s.h2}>Weitere Informationen</h2>
        <p style={s.p}>
          Details zur Verarbeitung personenbezogener Daten finden Sie in unserer{' '}
          <a href="/datenschutz" style={s.link}>Datenschutzerklärung</a>.
        </p>
      </>
    );
  }
  return (
    <>
      <h2 style={s.h2}>What are cookies?</h2>
      <p style={s.p}>
        Cookies are small text files stored on your device when you visit our website. They help us make the website
        functional, improve it, and provide you with a better user experience.
      </p>

      <h2 style={s.h2}>Which cookies do we use?</h2>

      <h3 style={s.h3}>1. Strictly necessary cookies (always active)</h3>
      <p style={s.p}>These cookies are essential for the website to function and cannot be disabled.</p>
      <ul style={s.ul}>
        <li><span style={s.strong}>Cart:</span> stores your selected items (duration: session)</li>
        <li><span style={s.strong}>Language:</span> your chosen language (duration: 12 months)</li>
        <li><span style={s.strong}>Authentication (Firebase Auth):</span> login status (duration: until logout)</li>
        <li><span style={s.strong}>Security token (CSRF):</span> protection against attacks (duration: session)</li>
      </ul>
      <p style={s.p}>Legal basis: Art. 6 (1)(b) and (f) GDPR, § 25 (2) TTDSG.</p>

      <h3 style={s.h3}>2. Functional cookies</h3>
      <ul style={s.ul}>
        <li><span style={s.strong}>Location preference:</span> delivery address for faster ordering (duration: 30 days)</li>
        <li><span style={s.strong}>Recent orders:</span> displaying your order history (duration: 30 days)</li>
      </ul>

      <h3 style={s.h3}>3. Analytics cookies (only with consent)</h3>
      <p style={s.p}>
        We currently do not use any third-party tracking or marketing cookies. Should this change in the future, we
        will obtain your explicit consent (Art. 6 (1)(a) GDPR, § 25 (1) TTDSG).
      </p>

      <h2 style={s.h2}>How can I manage cookies?</h2>
      <p style={s.p}>
        You can delete cookies at any time in your browser settings or block their storage. Please note that our
        website cannot function fully without strictly necessary cookies.
      </p>

      <h2 style={s.h2}>Further information</h2>
      <p style={s.p}>
        For details on the processing of personal data, please see our{' '}
        <a href="/datenschutz" style={s.link}>Privacy Policy</a>.
      </p>
    </>
  );
};

export default LegalPage;
